import { useState, useMemo } from "react"

const Changelog = ({ type, data }) => {
  // Filter and group data
  const filteredData = useMemo(() => {
    let filtered = data;
    if (type) {
      filtered = filtered.filter((item) => item.type === type);
    }

    // Group by date
    const grouped = filtered.reduce((acc, item) => {
      const date = new Date(item.createdAt).toDateString();
      if (!acc[date]) acc[date] = [];
      acc[date].push(item);
      return acc;
    }, {});

    // Sort dates descending and items by time descending
    const sortedGrouped = Object.keys(grouped)
      .sort((a, b) => new Date(b) - new Date(a))
      .reduce((acc, date) => {
        acc[date] = grouped[date].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        return acc;
      }, {});

    return sortedGrouped;
  }, [data, type]);

  // Type configurations
  const typeConfig = {
    added: {
      badgeClass: "badge bg-success",
      icon: "✨",
      label: "Added",
    },
    updated: {
      badgeClass: "badge bg-primary",
      icon: "🔄",
      label: "Updated",
    },
    fixed: {
      badgeClass: "badge bg-warning text-dark",
      icon: "🔧",
      label: "Fixed",
    },
    removed: {
      badgeClass: "badge bg-danger",
      icon: "🗑️",
      label: "Removed",
    },
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }

  const formatTime = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const isToday = (dateString) => {
    const today = new Date().toDateString()
    const itemDate = new Date(dateString).toDateString()
    return today === itemDate
  }

  const isYesterday = (dateString) => {
    const yesterday = new Date()
    yesterday.setDate(yesterday.getDate() - 1)
    const itemDate = new Date(dateString).toDateString()
    return yesterday.toDateString() === itemDate
  }

  const getRelativeDate = (dateString) => {
    if (isToday(dateString)) return "Today"
    if (isYesterday(dateString)) return "Yesterday"
    return formatDate(dateString)
  }

  return (
    <>
      {/* Bootstrap CSS - Add this to your HTML head or import in your app */}
      <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet" />

      <div className="container-fluid bg-light min-vh-100 py-4">
        <div className="container">

          {/* Changelog Entries */}
          <div className="changelog-timeline">
            {Object.keys(filteredData).length === 0 ? (
              <div className="text-center py-5">
                <div className="display-1 text-muted mb-3">📝</div>
                <h3 className="h4 fw-medium text-dark mb-2">No changelog entries found</h3>
                <p className="text-muted">Try adjusting your filters to see more results.</p>
              </div>
            ) : (
              Object.entries(filteredData).map(([date, entries]) => (
                <div key={date} className="mb-5">
                  {/* Date Header */}
                  <div
                    className="d-flex align-items-center mb-4 position-sticky"
                    style={{ top: "20px", zIndex: 10, backgroundColor: "#f8f9fa", paddingBottom: "1rem" }}
                  >
                    <div className="me-3">
                      <div className="bg-primary rounded-circle" style={{ width: "12px", height: "12px" }}></div>
                    </div>
                    <div>
                      <h2 className="h3 fw-semibold text-dark mb-1">{getRelativeDate(date)}</h2>
                      <p className="text-muted small mb-0">
                        {entries.length} update{entries.length !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>

                  {/* Entries for this date */}
                  <div className="ms-4">
                    {entries.map((log, idx) => {
                      const config = typeConfig[log.type] || {
                        badgeClass: "badge bg-secondary",
                        icon: "📄",
                        label: log.type,
                      }

                      return (
                        <div key={log._id || idx} className="card shadow-sm mb-4">
                          {/* Card Header */}
                          <div className="card-header bg-white border-bottom">
                            <div className="d-flex align-items-start justify-content-between">
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-center gap-3 mb-2">
                                  <span className={config.badgeClass}>
                                    <span className="me-1">{config.icon}</span>
                                    {config.label}
                                  </span>
                                  <small className="text-muted">{formatTime(log.createdAt)}</small>
                                </div>
                                <h3 className="h5 fw-semibold text-dark mb-2">{log.title}</h3>
                                {log.description && (
                                  <div className="text-muted" dangerouslySetInnerHTML={{ __html: log.description }} />
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Changes Content */}
                          {log.changes && Object.keys(log.changes).length > 0 && (
                            <div className="card-body">
                              {Object.entries(log.changes).map(([changeType, changeContent]) => (
                                <div key={changeType} className="mb-3">
                                  <h6 style={{fontWeight:600,fontSize:"20px"}}>Changes</h6>
                                  <div className="text-dark" dangerouslySetInnerHTML={{ __html: changeContent }} />
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Tags */}
                          {log.tags && log.tags.length > 0 && (
                            <div className="card-footer bg-light border-top">
                              <div className="d-flex flex-wrap gap-2">
                                {log.tags.map((tag, tagIdx) => (
                                  <span key={tagIdx} className="badge bg-light text-dark border">
                                    #{tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default Changelog
